import { type ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

/** How long the chip stays lit after its key is pressed. */
const PRESS_FLASH_MS = 140

/**
 * A keyboard-shortcut chip that FLASHES when its key is actually pressed.
 *
 * The flash is what connects the chip to the keyboard: a static badge beside a
 * label is decoration, one that lights up when you press the key is an
 * affordance you can verify. See docs/ui-effects.md §2.
 *
 * Only single, unmodified keys self-listen — a chord ("⌘K") would need to track
 * modifier state to avoid firing on every stray K, and the payoff is not worth
 * the misfires. Chord chips render, they just never light.
 */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  const [pressed, setPressed] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const label = typeof children === 'string' ? children : ''
  // A lone printable character, or one of the named keys we actually label.
  const targetKey = NAMED_KEYS[label.toLowerCase()] ?? (label.length === 1 ? label : null)

  useEffect(() => {
    if (!targetKey) return
    const expected = targetKey.length === 1 ? targetKey.toLowerCase() : targetKey
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const got = e.key.length === 1 ? e.key.toLowerCase() : e.key
      if (got !== expected) return
      setPressed(true)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setPressed(false), PRESS_FLASH_MS)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [targetKey])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  return (
    <kbd
      className={cn(
        'inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-sm border px-1',
        'font-sans text-[11px] leading-none',
        'transition-[transform,background-color,border-color,color,box-shadow] duration-100 ease-out',
        'motion-reduce:transition-none',
        pressed
          ? 'translate-y-px scale-[0.94] border-ring bg-accent text-accent-foreground shadow-none'
          : 'border-border bg-card text-muted-foreground shadow-[0_1px_0_var(--border)]',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

/** The named keys we print on chips, mapped to the `event.key` they produce. */
const NAMED_KEYS: Record<string, string> = {
  esc: 'Escape',
  enter: 'Enter',
  '↵': 'Enter',
  tab: 'Tab',
  '↑': 'ArrowUp',
  '↓': 'ArrowDown',
  '←': 'ArrowLeft',
  '→': 'ArrowRight',
}
